/**
 * Animate .ps-mobile-menu-shop-body open/close.
 * Native <details> skips CSS height transitions; height is set via max-height.
 */
(() => {
  if (window.__psMobileShopMenuInit) return;
  window.__psMobileShopMenuInit = true;

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const getBody = (details) => details.querySelector('.ps-mobile-menu-shop-body');
  const animationVersions = new WeakMap();

  const beginAnimation = (details) => {
    const version = (animationVersions.get(details) || 0) + 1;
    animationVersions.set(details, version);
    return version;
  };

  const isCurrentAnimation = (details, version) =>
    animationVersions.get(details) === version;

  const expand = (details) => {
    const body = getBody(details);
    if (!body) return;
    const version = beginAnimation(details);

    if (prefersReducedMotion()) {
      body.style.maxHeight = 'none';
      return;
    }

    body.style.maxHeight = '0px';
    // Double rAF so the browser paints 0 before expanding
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!details.open || !isCurrentAnimation(details, version)) return;
        body.style.maxHeight = `${body.scrollHeight}px`;
      });
    });
  };

  const collapse = (details, done) => {
    const body = getBody(details);
    if (!body) {
      done?.();
      return;
    }
    const version = beginAnimation(details);

    if (prefersReducedMotion()) {
      body.style.maxHeight = '0px';
      done?.();
      return;
    }

    const current = body.scrollHeight;
    body.style.maxHeight = `${current}px`;
    // Force reflow so the browser registers the start height
    void body.offsetHeight;
    body.style.maxHeight = '0px';

    let finished = false;
    const finish = (event) => {
      if (finished) return;
      if (event && event.propertyName && event.propertyName !== 'max-height') return;
      finished = true;
      body.removeEventListener('transitionend', finish);
      if (!isCurrentAnimation(details, version)) return;
      done?.();
    };

    body.addEventListener('transitionend', finish);
    window.setTimeout(finish, 450);
  };

  const bindShopDetails = (details) => {
    if (details.dataset.psShopBound === 'true') return;
    details.dataset.psShopBound = 'true';

    const summary = details.querySelector(':scope > summary');
    if (!summary) return;

    // Closing: animate first, then remove [open]
    summary.addEventListener('click', (event) => {
      if (!details.open) return;

      event.preventDefault();
      collapse(details, () => {
        details.open = false;
      });
    });

    // Opening: expand after [open] is applied
    details.addEventListener('toggle', () => {
      if (details.open) {
        expand(details);
      } else {
        const body = getBody(details);
        if (body) body.style.maxHeight = '0px';
      }
    });
  };

  const resetShopMenus = (root = document) => {
    root.querySelectorAll('.ps-mobile-menu-shop').forEach((details) => {
      beginAnimation(details);
      details.open = false;
      const body = getBody(details);
      if (body) body.style.maxHeight = '0px';
    });
  };

  const resetMobileMenu = (outer) => {
    resetShopMenus(outer);

    const scrollContainer = outer.querySelector('.ps-mobile-menu-scroll');
    if (scrollContainer) scrollContainer.scrollTop = 0;
  };

  const bindIndependentLocalizationPopover = (popover) => {
    if (popover.dataset.psMobileMenuIsolated === 'true') return;
    popover.dataset.psMobileMenuIsolated = 'true';

    popover.addEventListener('click', (event) => {
      const clickedOverlay = event.composedPath().some(
        (node) =>
          node instanceof Element &&
          node.getAttribute('part')?.split(/\s+/).includes('overlay')
      );

      // The popover is appended to <body> on mobile. Keep its clicks from
      // reaching the mobile menu's document-level outside-click listener.
      event.stopPropagation();

      if (clickedOverlay) {
        popover.hide?.();
      }
    });
  };

  const bindIndependentNewsletterPopup = (popup) => {
    if (popup.dataset.psMobileMenuIsolated === 'true') return;
    popup.dataset.psMobileMenuIsolated = 'true';

    // The menu disclosure closes on document-level outside clicks.
    // Keep newsletter interactions local so closing it does not close the menu.
    popup.addEventListener('click', (event) => {
      event.stopPropagation();
    });
  };

  const init = (root = document) => {
    root.querySelectorAll('.ps-mobile-menu-shop').forEach(bindShopDetails);
    root
      .querySelectorAll('.ps-localization-popover-ps-mobile-menu')
      .forEach(bindIndependentLocalizationPopover);
    root.querySelectorAll('.newsletter-popup').forEach(bindIndependentNewsletterPopup);

    // When the outer mobile panel closes, collapse Shop submenu
    root.querySelectorAll('.ps-mobile-menu-disclosure > details').forEach((outer) => {
      if (outer.dataset.psOuterBound === 'true') return;
      outer.dataset.psOuterBound = 'true';

      outer.addEventListener('focusout', (event) => {
        if (event.relatedTarget?.closest?.('.newsletter-popup')) {
          event.stopPropagation();
        }
      });

      outer.addEventListener('toggle', () => {
        resetMobileMenu(outer);
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });
})();
